import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import {
  IAuthContext,
  IAuthRequest,
  initialAuthContext,
} from "../interfaces/IAuthenticate";
import { IUser } from "../interfaces/IUser";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { handleMessage } from "../helpers/stateHelper";
import { useNavigate } from "react-router-dom";
import { onUserVerification } from "../helpers/authenticationHelper";
import { IDataContext } from "../interfaces/IDataContext";
import { ToastIntent } from "@fluentui/react-components";

const AuthContext = createContext<IAuthContext>(initialAuthContext);

export type AuthContextProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthContextProviderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<IUser | undefined>();
  useEffect(() => {
    const authenticatedUser = localStorage.getItem("authenticatedUser");
    if (authenticatedUser) {
      setUser(JSON.parse(authenticatedUser));
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (
    request: IAuthRequest,
    dataContext: IDataContext
  ): Promise<void> => {
    dataContext.setRootState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/customer/auth/login`,
        request,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response?.data?.user) {
        onUserVerification(
          response.data.user,
          dataContext,
          setUser,
          navigate,
          response.data
        );
      }
    } catch (error: any) {
      handleMessage(dataContext.setRootState, "error", t("error.swr"));
    } finally {
      dataContext.setRootState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const resetPassword = async (
    request: IAuthRequest,
    dataContext: IDataContext
  ): Promise<void> => {
    dataContext.setRootState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/customer/user/setpassword`,
        request,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${dataContext.state.authUser.token}`,
          },
        }
      );

      if (response?.data?.user) {
        onUserVerification(
          response.data.user,
          dataContext,
          setUser,
          navigate,
          response.data
        );
      }
    } catch (error: any) {
      handleMessage(dataContext.setRootState, "error", t("error.swr"));
    } finally {
      dataContext.setRootState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  };

  const logout = (
    dataContext: IDataContext,
    intent?: ToastIntent,
    msg?: string
  ) => {
    onUserVerification(false, dataContext, setUser, navigate);

    handleMessage(
      dataContext.setRootState,
      intent ?? "success",
      `${t(msg ?? "success.logout")}`
    );
  };

  const validateToken = async (token: string): Promise<boolean> => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/customer/auth/validate-token`,
        token,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      return false;
    }
  };

  const authContextValue: IAuthContext = {
    user,
    login,
    logout,
    resetPassword,
    validateToken,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
